import { ColumnDef } from '@tanstack/react-table';
import { DataTableColumnHeader } from '../DataTable/DataTableColumnHeader';
import { DataTableRowActions } from '../DataTable/DataTableRowActions';
import type { RowAction } from '../DataTable/DataTableRowActions';
import { PopOutList } from '../DataTable/PopOutList';
import { Badge } from '@/components/ui/badge';
import { Counter } from '@/components/ui/counter';
import { useTranslation } from '@hooks/useTranslation';
import { EmptySkuIcon, EditIcon, DeactivateIcon, CheckCircleIcon } from '../icons';
import { ACTIONS_COLUMN_WIDTH } from '../DataTable/constants';
import { INVENTORY_COLUMN_WIDTHS } from '../DataTable/columnWidths';
import type { Inventory, InventoryConfiguration, InventoryStatus } from './types';
import { getFormatLabel } from './formatOptions';
import { ConfigurationSummary } from './ConfigurationSummary';
import { formatCurrency } from '../../utils/formatters';

function ConfigurationLine({ config, inventoryName }: { config: InventoryConfiguration; inventoryName: string }) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-2 font-inter text-xs text-off-black">
      <span>{t('common.default_inventory.format_label', { value: getFormatLabel(config.default_event_type) || '—' })}</span>
      <ConfigurationSummary
        defaultType={config.default_type}
        quantity={config.quantity}
        value={config.value}
        multiplier={config.multiplier}
        inventoryName={inventoryName}
        size="compact"
      />
    </div>
  );
}

function ConfigurationCell({ configurations, inventoryName }: { configurations?: InventoryConfiguration[]; inventoryName: string }) {
  const { t } = useTranslation();

  if (!configurations || configurations.length === 0) {
    return <span className="text-sm text-grey">—</span>;
  }

  const isMulti = configurations.length > 1;

  return (
    <PopOutList
      width={INVENTORY_COLUMN_WIDTHS.defaultConfiguration}
      collapsed={
        <div className="flex flex-col gap-1.5">
          <ConfigurationLine config={configurations[0]} inventoryName={inventoryName} />
        </div>
      }
      header={
        isMulti ? (
          <div className="flex items-center gap-1.5">
            <span className="font-inter text-sm font-semibold leading-[1.5] text-grey">
              {t('common.table.configurations.other')}
            </span>
            <Counter variant="soft" value={configurations.length} />
          </div>
        ) : undefined
      }
      expanded={
        // PopOutList aligns this element's top with the cell's first row,
        // so do not add padding/margin above the first ConfigurationLine.
        <div className="flex flex-col">
          {configurations.map((config, index) => (
            <div key={config.id}>
              {index > 0 && <hr className="my-3 border-0 border-t border-dinnerplate-white" />}
              <ConfigurationLine config={config} inventoryName={inventoryName} />
            </div>
          ))}
        </div>
      }
    />
  );
}

function ActionsCell({
  inventory,
  isActive,
  onStatusToggle,
  onEdit,
}: {
  inventory: Inventory;
  isActive: boolean;
  onStatusToggle: (id: number, active: boolean) => void;
  onEdit: (id: number) => void;
}) {
  const { t } = useTranslation();

  const actions: RowAction[] = [
    {
      label: t('index.footer.edit'),
      icon: <EditIcon className="size-5" />,
      onClick: () => onEdit(inventory.id),
    },
    isActive
      ? {
          label: t('common.table.actions.deactivate'),
          icon: <DeactivateIcon className="size-5" />,
          onClick: () => onStatusToggle(inventory.id, false),
        }
      : {
          label: t('common.table.actions.activate'),
          icon: <CheckCircleIcon className="w-3.5 h-3.5" />,
          onClick: () => onStatusToggle(inventory.id, true),
        },
  ];

  return <DataTableRowActions actions={actions} />;
}

interface CreateInventoryColumnsOptions {
  status: InventoryStatus;
  onStatusToggle: (id: number, active: boolean) => void;
  onEdit: (id: number) => void;
}

export function createInventoryColumns({ status, onStatusToggle, onEdit }: CreateInventoryColumnsOptions): ColumnDef<Inventory>[] {
  const isActive = status === 'active';

  return [
    {
      accessorKey: 'name',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Item" />,
      size: INVENTORY_COLUMN_WIDTHS.name,
      meta: { frozen: true },
      cell: ({ row }) => (
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-input bg-dinnerplate-white overflow-hidden">
            {row.original.images && row.original.images.length > 0 ? (
              <img src={row.original.images[0].url} alt="" className="h-full w-full object-contain" />
            ) : (
              <EmptySkuIcon className="w-6 h-4 text-grey" />
            )}
          </div>
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-semibold text-off-black">{row.original.name}</span>
            {row.original.category && (
              <Badge
                variant="static"
                className="w-fit border-0 rounded-[5px] bg-dinnerplate-white text-grey text-xs font-semibold px-1.5 py-[3px] leading-[1.25]"
              >
                {row.original.category}
              </Badge>
            )}
          </div>
        </div>
      ),
    },
    {
      accessorKey: 'location',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Location" />,
      size: INVENTORY_COLUMN_WIDTHS.location,
      cell: ({ row }) => (
        <span className="text-sm">{row.original.location || '—'}</span>
      ),
    },
    {
      accessorKey: 'recommended_retail_price',
      header: 'Retail price',
      size: INVENTORY_COLUMN_WIDTHS.retailPrice,
      enableSorting: false,
      cell: ({ row }) => (
        <span className="text-sm">{formatCurrency(row.original.recommended_retail_price)}</span>
      ),
    },
    {
      accessorKey: 'cost_to_company',
      header: 'Cost price',
      size: INVENTORY_COLUMN_WIDTHS.costPrice,
      enableSorting: false,
      cell: ({ row }) => (
        <span className="text-sm">{formatCurrency(row.original.cost_to_company)}</span>
      ),
    },
    {
      id: 'default_inventory_configuration',
      header: 'Default inventory configuration',
      size: INVENTORY_COLUMN_WIDTHS.defaultConfiguration,
      enableSorting: false,
      cell: ({ row }) => (
        <ConfigurationCell configurations={row.original.inventory_configurations} inventoryName={row.original.name} />
      ),
    },
    {
      id: 'actions',
      enableSorting: false,
      size: ACTIONS_COLUMN_WIDTH,
      cell: ({ row }) => (
        <ActionsCell
          inventory={row.original}
          isActive={isActive}
          onStatusToggle={onStatusToggle}
          onEdit={onEdit}
        />
      ),
    },
  ];
}
